import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Project } from '../../../core/models/project.model';
import { environment } from '../../../../environments/environment';
import {
  ProjectHeaderComponent,
  MasterplanComponent,
  UnitTypesComponent,
  AmenitiesComponent,
  ProjectPaymentPlansComponent,
  ProjectGalleryComponent,
  ContactFormComponent,
  ActionButtonsComponent,
} from './components';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-projects-details',
  imports: [
    CommonModule,
    FormsModule,
    ProjectHeaderComponent,
    MasterplanComponent,
    UnitTypesComponent,
    AmenitiesComponent,
    ProjectPaymentPlansComponent,
    ProjectGalleryComponent,
    ContactFormComponent,
    ActionButtonsComponent,
    TranslatePipe,
  ],
  templateUrl: './projects-details.html',
  styleUrl: './projects-details.scss',
})
export class ProjectsDetails implements OnInit {
  isLoading = false;
  projectId = '';

  // Project data
  projectName = '';
  developer = '';
  location = '';
  description = '';

  deliveryDate = '';
  totalUnits = '';
  availableUnits = '';
  basePrice = 0;
  currency = 'EGP';
  averageRating = 0;
  totalReviews = 0;
  successRate = 0;
  successfulDeals = 0;
  responseTimeLabel = '';
  propertiesCount = 0;

  masterplanImage = '';

  unitTypes: any[] = [];
  amenities: string[] = [];
  paymentPlans: any[] = [];
  galleryImages: string[] = [];
  unitTypeNames: string[] = [];

  // Book Visit Modal State
  isVisitModalOpen = false;
  isSubmittingVisit = false;
  visitSuccessMessage = '';
  visitErrorMessage = '';
  visitForm = {
    name: '',
    phone: '',
    email: '',
    date: '',
    timeSlot: '12:00 PM',
    notes: '',
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.projectId = id;
      this.fetchProjectDetails(id);
    }

    // Get project data from navigation state
    const navigation = this.router.getCurrentNavigation();
    const navState = navigation?.extras.state as { property?: Project } | undefined;

    if (navState?.property) {
      this.applyProjectData(navState.property);
      return;
    }

    // Fallback: check window.history.state for browser refresh
    if (typeof window !== 'undefined') {
      const histState = (window.history?.state as { property?: Project }) || {};
      if (histState?.property) {
        this.applyProjectData(histState.property);
      }
    }
  }

  private formatImageUrl(url?: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `https://api.homigo-eg.com${cleanUrl}`;
  }

  private fetchProjectDetails(id: string): void {
    this.isLoading = true;
    this.http.get<any>(`${environment.api.baseUrl}/project/${id}`).subscribe({
      next: (res) => {
        if (res.status === 'success' && res.data) {
          const p = res.data.project;
          if (p) {
            this.projectName = p.name || this.projectName;
            this.description = p.description || this.description;
            this.location = p.location || this.location;
            this.deliveryDate = p.deliveryDate ? p.deliveryDate.split('T')[0] : this.deliveryDate;
            this.totalUnits = p.totalUnits !== undefined && p.totalUnits !== null ? p.totalUnits.toString() : this.totalUnits;
            this.availableUnits = p.availableUnits !== undefined && p.availableUnits !== null ? p.availableUnits.toString() : this.availableUnits;
            this.amenities = p.features || this.amenities;
            this.masterplanImage = this.formatImageUrl(p.masterPlan) || this.masterplanImage;
            this.galleryImages = Array.isArray(p.gallery) ? p.gallery.map((img: string) => this.formatImageUrl(img)) : this.galleryImages;
          }

          if (res.data.pricing) {
            this.basePrice = res.data.pricing.basePrice ?? this.basePrice;
            this.currency = res.data.pricing.currency || 'EGP';
          }

          if (res.data.paymentPlans) {
            this.paymentPlans = res.data.paymentPlans;
          }

          if (res.data.unitTypesSummary) {
            this.unitTypes = res.data.unitTypesSummary.map((u: any) => ({
              name: u.unitType || u.name || 'Unit',
              area: u.minArea && u.maxArea && u.minArea !== u.maxArea
                ? `${u.minArea} - ${u.maxArea} m²`
                : `${u.minArea ?? u.area ?? ''} m²`,
              price: u.price ? `${u.price} ${this.currency}` : `${this.basePrice} ${this.currency}`,
              available: u.availableCount !== undefined ? `${u.availableCount}` : (u.available ? `${u.available}` : '1'),
            }));
            this.unitTypeNames = this.unitTypes.map((u: any) => u.name).filter(Boolean);
          }

          if (res.data.owner?.performance) {
            const perf = res.data.owner.performance;
            this.averageRating = perf.averageRating || 0;
            this.totalReviews = perf.totalReviews || 0;
            this.successRate = perf.successRate || 0;
            this.successfulDeals = perf.successfulDeals || 0;
            this.propertiesCount = perf.propertiesCount || 0;
            this.responseTimeLabel = perf.responseTimeLabel || '';
          }
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        console.error('Failed to load project details');
      }
    });
  }

  private applyProjectData(project: Project): void {
    this.projectName = project.name || this.projectName;
    this.developer = project.developer || this.developer;
    this.location = project.location || this.location;
    this.deliveryDate = project.deliveryDate || this.deliveryDate;
    this.totalUnits = project.totalUnits?.toString() || this.totalUnits;

    if (project.imageUrl) {
      this.masterplanImage = project.imageUrl;
      this.galleryImages = [project.imageUrl, ...this.galleryImages.slice(1)];
    }

    if (project.unitTypes && project.unitTypes.length > 0) {
      this.unitTypeNames = project.unitTypes;
    }

    if (project.startingPrice) {
      this.basePrice = project.startingPrice;
      const formattedPrice = `From ${project.startingPrice.toLocaleString()} EGP`;
      this.unitTypes = this.unitTypes.map((unit, index) => ({
        ...unit,
        price: index === 0 ? formattedPrice : unit.price,
      }));
    }
  }

  // Action Button Handlers
  onRequestCall() {
    const el = document.getElementById('contactFormCard');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const phoneInput = document.getElementById('contactPhoneInput');
      if (phoneInput) phoneInput.focus();
    }
  }

  onBookVisit() {
    this.openVisitModal();
  }

  onGetBrochure() {
    if (this.masterplanImage) {
      window.open(this.masterplanImage, '_blank');
    } else if (this.galleryImages.length > 0) {
      window.open(this.galleryImages[0], '_blank');
    } else {
      this.onRequestCall();
    }
  }

  // Visit Modal logic
  openVisitModal(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.visitForm.date = tomorrow.toISOString().split('T')[0];
    this.visitSuccessMessage = '';
    this.visitErrorMessage = '';
    this.isVisitModalOpen = true;
  }

  closeVisitModal(): void {
    this.isVisitModalOpen = false;
  }

  submitBookVisit(): void {
    if (!this.visitForm.name.trim() || !this.visitForm.phone.trim() || !this.visitForm.date) {
      this.visitErrorMessage = 'يرجى إدخال الاسم، الهاتف، وتاريخ الزيارة';
      return;
    }
    if (!this.projectId) {
      this.visitErrorMessage = 'معرف المشروع غير متوفر';
      return;
    }

    this.isSubmittingVisit = true;
    this.visitErrorMessage = '';
    this.visitSuccessMessage = '';

    const scheduledDate = new Date(`${this.visitForm.date}T12:00:00Z`).toISOString();
    const payload = {
      targetType: 'project',
      targetId: this.projectId,
      requesterDetails: {
        name: this.visitForm.name.trim(),
        phone: this.visitForm.phone.trim(),
        email: this.visitForm.email.trim() || undefined,
      },
      scheduledDate,
      timeSlot: this.visitForm.timeSlot || '12:00 PM',
      notes: this.visitForm.notes.trim() || undefined,
    };

    this.http.post<any>(`${environment.api.baseUrl}/viewing`, payload).subscribe({
      next: (res) => {
        this.isSubmittingVisit = false;
        this.visitSuccessMessage = res?.message || 'تم حجز موعد الزيارة بنجاح! سيتواصل معك المطور لتأكيد الموعد.';
        setTimeout(() => {
          this.closeVisitModal();
        }, 2000);
      },
      error: (err) => {
        this.isSubmittingVisit = false;
        this.visitErrorMessage = err?.error?.message || 'فشل حجز موعد الزيارة، يرجى المحاولة لاحقاً.';
      },
    });
  }
}
